import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { getOriginalTranslationValue } from "@/helpers/languages";
import { createFormPermissions, toPermissionsRequest } from "@/helpers/permissions";
import { useSlug } from "@/hooks/useSlug";
import { documentMutations } from "@/queries/documents/mutations";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { routes } from "@/routes";

import { DocumentRootForm, type DocumentRootFormProps, type DocumentRootFormValues } from "./DocumentRootForm";

interface RouteParams extends Record<string, string | undefined> {
  folderId: string;
}

export function EditDocumentRootPage(): React.ReactNode {
  const slug = useSlug();
  const { folderId } = useParams<RouteParams>();
  const navigate = useNavigate();

  const documentQueries = useDocumentQueries();
  const permissionQueries = usePermissionQueries();

  const {
    data: roles = [],
    isFetching: rolesIsFetching,
    error: rolesError,
  } = useQuery({
    ...permissionQueries.listRoles(),
    notifyOnChangeProps: ["data", "isFetching", "error"],
  });
  const {
    data: rootFolderDetails,
    isFetching: rootFolderDetailsIsFetching,
    error: rootFolderDetailsError,
  } = useQuery({
    ...documentQueries.rootFolderDetails({ folderId: folderId! }),
    enabled: true,
    notifyOnChangeProps: ["data", "isFetching", "error"],
  });
  const {
    data: defaultFolderPermissions = [],
    isFetching: defaultFolderPermissionsIsFetching,
    error: defaultFolderPermissionsError,
  } = useQuery({
    ...documentQueries.defaultFolderPermissions(),
    notifyOnChangeProps: ["data", "isFetching", "error"],
  });

  const editRootMutation = documentMutations.useUpdateRootFolder();

  const loading = rootFolderDetailsIsFetching || rolesIsFetching || defaultFolderPermissionsIsFetching;
  if (loading) {
    return <FullSizeLoader withPadding />;
  }

  const error = rootFolderDetailsError || rolesError || defaultFolderPermissionsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const onSubmit = (data: DocumentRootFormValues) => {
    if (!folderId) {
      return;
    }

    const body = {
      name: data.name,
      description: data.description,
      nameTranslations: [],
      descriptionTranslations: [],
      audience: data.audience,
      iconChar: data.iconChar,
      permissions: toPermissionsRequest(data.permissions),
    };

    return editRootMutation.mutate(
      { id: folderId, body },
      {
        onSuccess: () => {
          void navigate(routes.documents.rootList({ slug }));
        },
      },
    );
  };

  const defaultValues: DocumentRootFormValues = {
    name: rootFolderDetails
      ? getOriginalTranslationValue(rootFolderDetails.nameTranslations, rootFolderDetails.originalLanguageId)
      : "",
    description: rootFolderDetails
      ? getOriginalTranslationValue(rootFolderDetails.descriptionTranslations, rootFolderDetails.originalLanguageId)
      : "",
    audience: rootFolderDetails?.audience || [],
    permissions: createFormPermissions(roles, defaultFolderPermissions, rootFolderDetails?.permissions),
    iconChar: rootFolderDetails?.iconChar || "\uEECA",
  };

  const formProps: DocumentRootFormProps = {
    originalLanguageId: rootFolderDetails?.originalLanguageId,
    onSubmit,
    defaultValues,
    isSubmitting: editRootMutation.isPending,
    isEditMode: true,
    folderId,
  };

  return <DocumentRootForm {...formProps} />;
}
